import uniq                                                 from 'lodash-es/uniq';
import { CheckboxWithOptionsValue,
  CheckboxWithoutOptions,
  Filter,
  FILTER_TYPE,
  FilterGroup,
  FilterInfo,
  FilterPanelState,
  Group,
  GROUP_NAME,
  LeadFilterDetails,
  RequestFilter,
  SelectedFilter,
  SelectedFiltersValues,
  SelectiveRange,
  TAB,
  Tab,
  TrackedTab }                                                           from '@/components/filter-panel/filter-panel.types';
import { AvailableFilterOption, AvailableFilterOrFunction } from '@/views/lead-time/lead-time.types';
import { ObjectWithStringKeys }                             from '@/types';
import { DTEOP, LEAD_TIME }                                 from '@/helpers/variables';

export const leadFilterFunctionsName = 'functions';

export const defaultLeadFilter: LeadFilterDetails = {
  groupName: GROUP_NAME.FUNCTION,
  tabName: TAB.FUNCTION,
  name: leadFilterFunctionsName,
};

export const convertCheckboxGroupToList = (element: FilterGroup): (string | number)[] => {
  switch (element.contentType) {
  case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
    return element.values
      .reduce((acc: string[], curr: CheckboxWithOptionsValue) => (curr.isSelected ? [ ...acc, curr.name ] : acc), []);
  case FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS:
    // eslint-disable-next-line no-nested-ternary
    element.values.sort((a, b) => (a.isSelected === b.isSelected ? 0 : a.isSelected ? -1 : 1));

    return element.values
      .reduce((acc: string[], curr: CheckboxWithOptionsValue) => (curr.isSelected ? [ ...acc, curr.name ] : acc), []);
  case FILTER_TYPE.MULTI_DATE_RANGE:
    return element.values
      .reduce((acc: number[], curr: SelectiveRange) => (curr.isSelected ? [ ...acc, element.values.indexOf(curr) ] : acc), []);
  default:
    return [];
  }
};

export const findElementById = (groups: Group[], uuid: string): Filter | undefined => {
  let element: Filter | undefined;

  groups.some((group: Group): boolean => group.tabs.some((tab: Tab): boolean => {
    const matchedElement: Filter | undefined = tab.content.find((element: Filter) => element.uuid === uuid);
    if (matchedElement) {
      element = matchedElement;
      return true;
    }
    return false;
  }));

  return (element && element);
};

export const findElementsByContentType = (filterType: FILTER_TYPE, groups: Group[]): Filter[] => {
  const elements: Filter[] = [];

  groups.forEach((group: Group) => {
    group.tabs.forEach((tab: Tab) => {
      tab.content.forEach((filter: Filter) => {
        if (filter.contentType === filterType) {
          elements.push(filter);
        }
      });
    });
  });

  return elements;
};

export const findGroupByName = (groups: Group[], groupName: string): Group | undefined => groups.find((group: Group) => group.groupName === groupName);

export const findTabByName = (tabs: Tab[], tabName: string): Tab | undefined => tabs.find((tab: Tab) => tab.tabName === tabName);

export const doesFilterWithOptionsContainValues = (element: Filter): boolean => {
  if (element.contentType === FILTER_TYPE.CHECKBOX_WITH_OPTIONS) {
    return !!element.values.length;
  }
  return true;
};

export const doesTabContainFilter = (group: Group, tab: Tab): boolean => (
  tab.content.length !== 0 && tab.content.some((element: Filter): boolean => (
    Object.values(FILTER_TYPE)
      .includes(element.contentType)
    && Object.values(GROUP_NAME)
      .includes((group as Group).groupName)
    && doesFilterWithOptionsContainValues(element)
  )));

export const checkIfMultipleSameFiltersAndReturnDetails = (groups: Group[], filterName: string): FilterInfo => {
  interface InternalFilterInfo {
    filterCounter: number;
    groupNamesPresence: Set<string>;
    tabNamesPresence: Set<string>;
  }

  const filterInfo: InternalFilterInfo = {
    filterCounter: 0,
    groupNamesPresence: new Set(),
    tabNamesPresence: new Set(),
  };

  groups.forEach((group: Group) => {
    group.tabs.forEach((tab: Tab) => {
      tab.content.forEach((filter: Filter) => {
        if (filter.name === filterName) {
          filterInfo.filterCounter += 1;
          filterInfo.groupNamesPresence.add(group.groupName);
          filterInfo.tabNamesPresence.add(tab.tabName);
        }
      });
    });
  });

  return {
    filterCounter: 0,
    groupNamesPresence: [ ...filterInfo.groupNamesPresence ],
    tabNamesPresence: [ ...filterInfo.tabNamesPresence ],
  };
};


export const getValuesOfFiltersByNameAndExcludeOneTab = (groups: Group[], filterNameToFind: string, tabNameToExclude: string): SelectedFiltersValues => {
  const valuesArray: Set<boolean | string | { min: string; max: string }> = new Set();
  groups.forEach((group: Group) => {
    group.tabs.forEach((tab: Tab) => {
      if (tab.tabName !== tabNameToExclude) {
        tab.content.forEach((filter: Filter) => {
          if (filter.name === filterNameToFind) {
            switch (filter.contentType) {
            case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
              filter.values.forEach((value: CheckboxWithOptionsValue) => {
                if (value.isSelected) {
                  valuesArray.add(value.name);
                }
              });
              break;
            case FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS:
              filter.values.forEach((value: CheckboxWithOptionsValue) => {
                if (value.isSelected) {
                  valuesArray.add(value.name);
                }
              });
              break;
            case FILTER_TYPE.CHECKBOX_WITHOUT_OPTIONS:
              valuesArray.add(filter.isSelected);
              break;
            case FILTER_TYPE.MULTI_DATE_RANGE:
              filter.values.forEach((value: SelectiveRange) => {
                if (value.isSelected) {
                  valuesArray.add({ min: value.from, max: value.to });
                }
              });
              break;
            default:
              break;
            }
          }
        });
      }
    });
  });
  return [ ...valuesArray ];
};

export const filterSelectedFilters = (selectedFilters: SelectedFilter[], groupName: string, tabName: string, groups: Group[]): SelectedFilter[] => (
  selectedFilters.reduce((acc: SelectedFilter[], curr: SelectedFilter) => {
    const { groupName: filterGroupName, tabName: filterTabName } = curr;

    if (groupName !== filterGroupName) {
      const group: Group | undefined = findGroupByName(groups, filterGroupName);
      let appliedTabInGroup!: string;
      /* eslint-disable-next-line no-unused-expressions */
      group?.tabs.forEach((tab: Tab) => {
        if (tab.isApplied) {
          appliedTabInGroup = tab.tabName;
        }
      });
      if (filterTabName === appliedTabInGroup) {
        return [ ...acc, curr ];
      }
      return acc;
    }
    if (tabName === filterTabName) {
      return [ ...acc, curr ];
    }

    return acc;
  }, []));

export const createGroupsBasedOnAvailableFiltersAndFunctions = (
  allPossibleFilters: Group[],
  availableFiltersAndFunctions: ObjectWithStringKeys<AvailableFilterOrFunction>,
  addTracking = true,
  applySingleTab = true,
): Group[] => {
  const groupsWithAvailableFilters: Group[] = [];
  allPossibleFilters.forEach((group: Group) => {
    const groupToPush: Group = {
      ...group,
      tabs: [],
    };
    group.tabs.forEach((tab: Tab, index: number) => {
      const tabToPush: Tab = {
        ...tab,
        isApplied: index === 0 && applySingleTab,
        content: [],
      };
      if (group.groupName === GROUP_NAME.GENERAL && addTracking) {
        tabToPush.isTracked = index === 0;
      }
      tab.content.forEach((filter: Filter) => {
        if (availableFiltersAndFunctions[ filter.name ] !== undefined) {
          switch (filter.contentType) {
          case FILTER_TYPE.CHECKBOX_WITH_OPTIONS: {
            const contentToPush: Filter = {
              ...filter,
              label: availableFiltersAndFunctions[ filter.name ].label,
              values: [],
            };
            contentToPush.values = (availableFiltersAndFunctions[ filter.name ].options as AvailableFilterOption[]).map((option: AvailableFilterOption) => ({
              ...option,
              label: option.label,
              isDefault: option.isDefault,
              isSelected: option.isDefault,
              order: option.order,
              color: option.color,
            }));
            if (contentToPush.values.length) {
              tabToPush.content.push(contentToPush);
            }
            break;
          }
          case FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS: {
            const contentToPush: Filter = {
              ...filter,
              label: availableFiltersAndFunctions[ filter.name ].label,
              values: [],
            };
            contentToPush.values = (availableFiltersAndFunctions[ filter.name ].options as AvailableFilterOption[]).map((option: AvailableFilterOption) => ({
              ...option,
              label: option.label,
              isDefault: option.isDefault,
              isSelected: option.isDefault,
              order: option.order,
              color: option.color,
            }));
            tabToPush.content.push(contentToPush);
            break;
          }
          case FILTER_TYPE.MULTI_DATE_RANGE: {
            const contentToPush: Filter = {
              ...filter,
              label: availableFiltersAndFunctions[ filter.name ].label,
              values: [{ isSelected: false, from: '', to: '' }],
            };
            tabToPush.content.push(contentToPush);
            break;
          }
          case FILTER_TYPE.CHECKBOX_WITHOUT_OPTIONS: {
            const contentToPush: Filter = {
              ...filter,
              label: availableFiltersAndFunctions[ filter.name ].label,
              color: availableFiltersAndFunctions[ filter.name ].color,
              order: availableFiltersAndFunctions[ filter.name ].order,
              isDefault: (availableFiltersAndFunctions[ filter.name ].isDefault as boolean),
              isSelected: (availableFiltersAndFunctions[ filter.name ].isDefault as boolean),
            };
            tabToPush.content.push(contentToPush);
            break;
          }
          case FILTER_TYPE.DATE_RANGE:
          case FILTER_TYPE.LEAD_TIME: {
            const contentToPush: Filter = {
              ...filter,
              label: availableFiltersAndFunctions[ filter.name ].label,
              from: '',
              to: '',
            };
            tabToPush.content.push(contentToPush);
            break;
          }
          case FILTER_TYPE.SINGLE_DATE: {
            const contentToPush: Filter = {
              ...filter,
              label: availableFiltersAndFunctions[ filter.name ].label,
            };
            tabToPush.content.push(contentToPush);
            break;
          }
          default:
            break;
          }
        }
      });
      if (tabToPush.content.length) {
        if (tabToPush.tabName === TAB.FUNCTION) {
          const atLeastOneFunctionIsSelected: boolean = tabToPush.content.some((functionElement: Filter) => (functionElement as CheckboxWithoutOptions).isSelected);

          if (!atLeastOneFunctionIsSelected) {
            tabToPush.content.forEach((functionElement: Filter) => {
              (functionElement as CheckboxWithoutOptions).isSelected = true;
            });
          }
        }

        groupToPush.tabs.push(tabToPush);
      }
    });
    if (groupToPush.tabs.length) {
      groupsWithAvailableFilters.push(groupToPush);
    }
  });
  return groupsWithAvailableFilters;
};

export const getTrackedTabs = (groups: Group[]): TrackedTab[] => groups.reduce((acc: TrackedTab[], curr: Group) => {
  const trackedTabs: TrackedTab[] = [];
  curr.tabs.forEach((tab: Tab) => {
    if (tab.isTracked) {
      trackedTabs.push({ tab: tab.tabName, group: curr.groupName });
    }
  });
  return [ ...acc, ...trackedTabs ];
}, []);

export const getAppliedTabs = (groups: Group[]): string[] => groups.reduce((accGroups: string[], currGroup: Group) => {
  const activeTabs: string[] = currGroup.tabs.reduce((accTabs: string[], currTab: Tab) => {
    if (currTab.isApplied || currTab.isTracked) {
      return [ ...accTabs, currTab.tabName ];
    }

    return [ ...accTabs ];
  }, []);

  return [ ...accGroups, ...activeTabs ];
}, []);

export const getGroupsWithTracking = (groups: Group[]): string[] => groups.reduce((acc: string[], curr: Group) => {
  const trackedGroups: string[] = [];
  curr.tabs.forEach((tab: Tab) => {
    if (Object.prototype.hasOwnProperty.call(tab, 'isTracked')) {
      trackedGroups.push(curr.groupName);
    }
  });
  return uniq([ ...acc, ...trackedGroups ]);
}, []);

export const normalizeFilters = (filters: SelectedFilter[]): RequestFilter[] => filters.reduce((acc: RequestFilter[], curr: SelectedFilter) => {
  const { tabName, groupName, uuid, ...others } = curr;
  return [ ...acc, others ];
}, []);

export const getAppliedTabInGivenGroup = (groups: Group[], groupName: string): string => {
  const group: Group | undefined = findGroupByName(groups, groupName);
  if (group) {
    return group.tabs.reduce((acc: string, curr: Tab) => {
      if (curr.isApplied) {
        return curr.tabName;
      }
      return acc;
    }, '');
  }
  return '';
};

export const getTabsForWhichCallsNeedToBePerformed = (allTrackedTabs: TrackedTab[], currentlyAppliedTab: string): string[] => {
  if (!allTrackedTabs.length) {
    return [ currentlyAppliedTab ];
  }
  const allTrackedTabsNames: string[] = allTrackedTabs.reduce((acc: string[], curr: TrackedTab) => [ ...acc, curr.tab ], []);

  const isCurrentlyAppliedTabInTrackedTabs: boolean = allTrackedTabs.some((trackedTab: TrackedTab) => trackedTab.tab === currentlyAppliedTab);
  if (isCurrentlyAppliedTabInTrackedTabs) {
    return allTrackedTabsNames;
  }
  return [ ...allTrackedTabsNames, currentlyAppliedTab ];
};

export const composeFiltersToSend = (filterPanelStore: FilterPanelState, groupName: string, tabName: string): RequestFilter[] => {
  const filters: SelectedFilter[] = filterSelectedFilters(
    filterPanelStore.selectedFilters,
    groupName,
    tabName,
    filterPanelStore.groups,
  );

  return normalizeFilters(filters);
};

export const findLeadFilterAndSetValue = (groups: Group[], groupName: string, tabName: string, filterName: string, value: boolean): void => {
  const group: Group | undefined = findGroupByName(groups, groupName);
  if (group) {
    const tab: Tab | undefined = findTabByName(group.tabs, tabName);
    if (tab) {
      if (filterName !== leadFilterFunctionsName) {
        const filter: Filter | undefined = tab.content.find((filter: Filter) => filter.name === filterName);
        if (filter) {
          filter.isLead = value;
        }
      } else {
        tab.content.forEach((fn: Filter) => {
          fn.isLead = value;
        });
      }
    }
  }
};

export const convertToDateRangePickerFormat = <T extends { from: string; to: string }>(value: T): string[] => [ value.from, value.to ];

export const createSelectedElement = (group: Group,
  tab: Tab,
  element: Filter,
  groupName: string,
  view: string,
  state: FilterPanelState): SelectedFilter => {
  const implementedViews: string[] = [ LEAD_TIME, DTEOP ];

  const selectedElement: SelectedFilter = {
    values: [],
    labels: [],
    groupName: group.groupName,
    tabName: tab.tabName,
    columnName: element.name,
    type: undefined,
    uuid: element.uuid,
  };

  if (Object.prototype.hasOwnProperty.call(element, 'isExclusive')) {
    selectedElement.exclusive = element.isExclusive;
  }

  if (Object.prototype.hasOwnProperty.call(element, 'isLead')) {
    selectedElement.isLead = element.isLead;
  }

  switch (element.contentType) {
  case FILTER_TYPE.CHECKBOX_WITH_OPTIONS:
    element.values.forEach((value: CheckboxWithOptionsValue) => {
      selectedElement.type = FILTER_TYPE.CHECKBOX_WITH_OPTIONS;
      if (value.isSelected) {
        if (selectedElement.labels) {
          selectedElement.labels.push({ originalName: value.name, label: value.label });
        }
        selectedElement.values.push(value.name);
      }
    });
    break;
  case FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS:
    element.values.forEach((value: CheckboxWithOptionsValue) => {
      selectedElement.type = FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS;
      if (value.isSelected) {
        if (selectedElement.labels) {
          selectedElement.labels.push({ originalName: value.name, label: value.label });
        }
        selectedElement.values.push(value.name);
      }
    });
    break;
  case FILTER_TYPE.CHECKBOX_WITHOUT_OPTIONS:
    if (groupName === GROUP_NAME.FUNCTION && implementedViews.includes(view as string)) {
      state.selectedFunctions = tab.content.reduce((acc: string[], curr: Filter) => {
        if ((curr as CheckboxWithOptionsValue).isSelected) {
          return [ ...acc, curr.name ];
        }
        return acc;
      }, []);
    } else if (element.isSelected) {
      selectedElement.type = FILTER_TYPE.CHECKBOX_WITHOUT_OPTIONS;
      selectedElement.values = [ element.isSelected ];
      selectedElement.columnName = element.name;
    }
    break;
  case FILTER_TYPE.MULTI_DATE_RANGE:
    selectedElement.type = FILTER_TYPE.MULTI_DATE_RANGE;
    selectedElement.columnName = element.name;
    element.values.forEach((value: any) => {
      if (value.from && value.to && value.isSelected) {
        selectedElement.values.push({ min: value.from, max: value.to });
      }
    });
    break;
  default:
    break;
  }

  return selectedElement;
};
